/** @jsx jsx */
import React, { useState, useEffect } from "react";
import { css, jsx } from "@emotion/react";
import { 
  Box, 
  Container, 
  InputBase, 
  IconButton, 
  Select, 
  MenuItem, 
  FormControl, 
  Typography,
  useTheme,
  CircularProgress,
  Grid,
  useMediaQuery
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { postRecord } from "services/services";

const API_GetAllLocations = "locations/getall";

export default function TopHeader() {
  const [location, setLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  useEffect(() => {
    let componentMounted = true;
    
    const fetchLocations = async () => {
      try {
        const response = await postRecord(API_GetAllLocations, {});
        if (response.status === 200 && response.data && componentMounted) {
          setLocations(response.data);
          // Set default location if available
          if (response.data.length > 0) {
            setLocation(response.data[0].Location_Id);
          }
        }
        if (componentMounted) {
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
        if (componentMounted) {
          setLoading(false);
        }
      }
    };

    fetchLocations();
    
    return () => {
      componentMounted = false;
    };
  }, []);

  const handleLocationChange = (event) => {
    setLocation(event.target.value);
  };

  // Find the selected location object
  const selectedLocation = locations.find(loc => loc.Location_Id === location);

  return (
    <Box 
      sx={{
        width: "100%",
        height: "50px",
        borderBottom: "1px solid",
        borderColor: "divider"
      }}
    >
      <Container 
        maxWidth="xl"
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <Grid container alignItems="center" justifyContent="space-between">
          {/* Left side - can be used for announcements or branding */}
          <Grid item xs={12} sm="auto">
            <Box>
              {/* <Typography variant="body2" color="text.secondary">
                Free shipping on orders over $50
              </Typography> */}
            </Box>
          </Grid>
          
          {/* Right side - search and location */}
          <Grid item xs={12} sm="auto">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2
              }}
            >
              {/* Search control */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "4px",
                  border: "1px solid",
                  borderColor: "divider",
                  px: 1,
                  height: "32px",
                }}
              >
                <InputBase
                  placeholder="Search..."
                  sx={{ 
                    ml: 1,
                    flex: 1,
                    fontSize: "0.875rem",
                    width: "250px"
                  }}
                />
                <IconButton type="button" sx={{ p: '4px' }} aria-label="search">
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Box>
              
              {/* Location dropdown */}
              <FormControl 
                variant="standard"
                sx={{ 
                  minWidth: 120,
                  borderBottom: "none"
                }}
              >
                <Select
                  value={location}
                  onChange={handleLocationChange}
                  displayEmpty
                  disableUnderline
                  IconComponent={KeyboardArrowDownIcon}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationOnIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                      {!isMobile && (
                        loading ? (
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                        ) : selectedLocation ? (
                          <Typography variant="body2" noWrap>
                            {selectedLocation.Location_Name}
                          </Typography>
                        ) : (
                          <Typography variant="body2" noWrap>
                            Select Location
                          </Typography>
                        )
                      )}
                    </Box>
                  )}
                  sx={{
                    fontSize: "0.875rem", 
                    height: "32px",
                    '.MuiSelect-select': { 
                      p: 0,
                      display: 'flex',
                      alignItems: 'center'
                    }
                  }}
                >
                  {loading ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading locations...
                    </MenuItem>
                  ) : locations.length > 0 ? (
                    locations.map((loc) => (
                      <MenuItem key={loc.Location_Id} value={loc.Location_Id} sx={{ fontSize: "0.875rem" }}>
                        {loc.Location_Name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No locations available</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
